/**
 * Audio Player Context for NLT Radio
 * Manages global audio playback state
 */

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AudioPlayerContext = createContext(null);

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};

export const AudioPlayerProvider = ({ children }) => {
  const audioRef = useRef(new Audio());
  const handleNextRef = useRef(null);
  const fadeRef = useRef(null);
  
  const [currentTrack, setCurrentTrack] = useState(null);
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1); // For chopped & screwed / sped up
  const [isShuffled, setIsShuffled] = useState(false);
  const [repeatMode, setRepeatMode] = useState('none'); // 'none', 'all', 'one'
  const [queueEnded, setQueueEnded] = useState(false); // set when a genre queue runs out

  // Audio event listeners
  useEffect(() => {
    const audio = audioRef.current;

    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleDurationChange = () => setDuration(audio.duration || 0);
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const handleEnded = () => {
      if (handleNextRef.current) handleNextRef.current();
    };
    const handleError = (e) => {
      console.error('Audio error:', e);
      setIsLoading(false);
      setIsPlaying(false);
    };

    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, []);

  // Track play for analytics
  const trackPlay = useCallback(async (track) => {
    if (!track?.id) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Record play event
      await axios.post(
        `${API_URL}/api/radio/algorithm/track-play`,
        {
          track_id: track.id,
          genre: track.genre,
          artist_id: track.artist_id
        },
        { headers }
      ).catch(() => {});
      
      // Increment play count
      await axios.post(
        `${API_URL}/api/artists/radio/tracks/${track.id}/play`,
        {},
        { headers }
      ).catch(() => {});
    } catch (error) {
      // Silent fail for analytics
    }
  }, []);

  // Play a track
  // Smoothly ramp the audio volume to a target over `ms` (used for cross-fade transitions)
  const fadeTo = useCallback((target, ms, onDone) => {
    const audio = audioRef.current;
    if (fadeRef.current) { clearInterval(fadeRef.current); fadeRef.current = null; }
    const start = audio.volume;
    const steps = Math.max(1, Math.round(ms / 40));
    let i = 0;
    fadeRef.current = setInterval(() => {
      i++;
      const v = start + (target - start) * (i / steps);
      audio.volume = Math.max(0, Math.min(1, v));
      if (i >= steps) { clearInterval(fadeRef.current); fadeRef.current = null; onDone && onDone(); }
    }, 40);
  }, []);

  const playTrack = useCallback((track, newPlaylist = null, index = 0) => {
    if (!track?.audio_url) {
      console.error('No audio URL for track');
      return;
    }

    const audio = audioRef.current;

    // Update playlist if provided
    if (newPlaylist) {
      setPlaylist(newPlaylist);
      setCurrentIndex(index);
    }

    const targetVol = isMuted ? 0 : volume;

    // Load the new track and fade it IN
    const startNew = () => {
      audio.src = track.audio_url;
      audio.playbackRate = playbackRate;
      audio.volume = 0;
      setCurrentTrack(track);
      audio.play()
        .then(() => {
          setIsPlaying(true);
          trackPlay(track);
          fadeTo(targetVol, 350);
        })
        .catch(error => {
          console.error('Play failed:', error);
          audio.volume = targetVol; // restore so a later play isn't silent
          setIsPlaying(false);
        });
    };

    // Cross-fade: if something is already playing, dip the current track out first,
    // then swap and fade the new one in — a smooth DJ-style transition, no hard cut.
    if (!audio.paused && audio.src) {
      fadeTo(0, 220, startNew);
    } else {
      startNew();
    }
  }, [playbackRate, volume, isMuted, trackPlay, fadeTo]);

  // Toggle play/pause
  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else if (currentTrack) {
      audio.play()
        .then(() => setIsPlaying(true))
        .catch(error => console.error('Play failed:', error));
    }
  }, [isPlaying, currentTrack]);

  // Pause
  const pause = useCallback(() => {
    audioRef.current.pause();
    setIsPlaying(false);
  }, []);

  // Next track
  const handleNext = useCallback(() => {
    if (playlist.length === 0) return;

    let nextIndex;
    if (repeatMode === 'one') {
      nextIndex = currentIndex;
    } else if (isShuffled) {
      nextIndex = Math.floor(Math.random() * playlist.length);
    } else {
      nextIndex = (currentIndex + 1) % playlist.length;
      if (nextIndex === 0 && repeatMode === 'none') {
        setQueueEnded(true); // ran out of tracks in this (genre) queue
        pause();
        return;
      }
    }

    setCurrentIndex(nextIndex);
    playTrack(playlist[nextIndex], null, nextIndex);
  }, [playlist, currentIndex, repeatMode, isShuffled, playTrack, pause]);

  // Keep the latest handleNext available to the (once-mounted) 'ended' listener
  useEffect(() => {
    handleNextRef.current = handleNext;
  }, [handleNext]);

  // Previous track
  const handlePrevious = useCallback(() => {
    if (playlist.length === 0) return;

    // If more than 3 seconds in, restart current track
    if (currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }

    const prevIndex = currentIndex === 0 ? playlist.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    playTrack(playlist[prevIndex], null, prevIndex);
  }, [playlist, currentIndex, currentTime, playTrack]);

  // Seek to position
  const seekTo = useCallback((time) => {
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  }, []);

  // Set volume
  const setVolumeLevel = useCallback((level) => {
    if (fadeRef.current) { clearInterval(fadeRef.current); fadeRef.current = null; }
    const clampedLevel = Math.max(0, Math.min(1, level));
    audioRef.current.volume = clampedLevel;
    setVolume(clampedLevel);
    if (clampedLevel > 0) setIsMuted(false);
  }, []);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (fadeRef.current) { clearInterval(fadeRef.current); fadeRef.current = null; }
    const newMuted = !isMuted;
    audioRef.current.volume = newMuted ? 0 : volume;
    setIsMuted(newMuted);
  }, [isMuted, volume]);

  // Set playback rate (for effects)
  const setSpeed = useCallback((rate) => {
    // 0.8 = chopped & screwed, 1.0 = normal, 1.25 = sped up
    const clampedRate = Math.max(0.5, Math.min(2, rate));
    audioRef.current.playbackRate = clampedRate;
    setPlaybackRate(clampedRate);
  }, []);

  // Toggle shuffle
  const toggleShuffle = useCallback(() => {
    setIsShuffled(prev => !prev);
  }, []);

  // Cycle repeat mode
  const cycleRepeat = useCallback(() => {
    setRepeatMode(prev => {
      if (prev === 'none') return 'all';
      if (prev === 'all') return 'one';
      return 'none';
    });
  }, []);

  const value = {
    // State
    currentTrack,
    playlist,
    currentIndex,
    isPlaying,
    duration,
    currentTime,
    volume,
    isMuted,
    isLoading,
    playbackRate,
    isShuffled,
    repeatMode,
    queueEnded,
    
    // Actions
    playTrack,
    togglePlayPause,
    pause,
    handleNext,
    handlePrevious,
    seekTo,
    setVolumeLevel,
    toggleMute,
    setSpeed,
    toggleShuffle,
    cycleRepeat,
    setRepeatMode,
    setQueueEnded,
    setPlaylist
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
};

export default AudioPlayerContext;
